import { getDevAuthHeaders } from '../auth/dev-impersonation';
import { getNativeBearerAuthHeaders } from '../auth/native-api-auth';
import { getApiBaseUrl } from '../env';
import { apiErrorFromResponse } from '../net/api-error';
import { reportNetworkError, setServerReachable } from '../net/net-status';

import {
  getCachedPageDocument,
  setCachedPageDocument,
  type PageDocument,
} from './page-cache';
import type { BlockRecord, MemberRecord } from './types';

function readTimeoutMs(): number {
  // import.meta.env is undefined under the node unit-test runner (no Vite).
  const raw = import.meta.env?.VITE_API_TIMEOUT_MS as string | undefined;
  const parsed = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 60000;
}

const REQUEST_TIMEOUT_MS = readTimeoutMs();

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const method = init?.method ?? 'GET';
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const start = Date.now();
  try {
    const res = await fetch(`${base}${path}`, {
      ...init,
      credentials: 'include',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...getDevAuthHeaders(),
        ...(await getNativeBearerAuthHeaders()),
        ...init?.headers,
      },
    });
    console.log('[CS-NET]', method, path, res.status, `${Date.now() - start}ms`);
    setServerReachable(true);
    if (res.status >= 500) {
      reportNetworkError({
        message: 'The server had a problem completing a request.',
        detail: `${method} ${path} → ${res.status}`,
      });
    }
    return res;
  } catch (err) {
    const aborted = controller.signal.aborted;
    const detail = aborted ? `timeout after ${REQUEST_TIMEOUT_MS}ms` : err instanceof Error ? err.message : String(err);
    console.error('[CS-NET] failed', method, path, `${Date.now() - start}ms`, detail);
    setServerReachable(false);
    reportNetworkError({
      message: aborted
        ? 'A request timed out. Your connection may be unstable.'
        : 'Could not reach the server. Check your connection.',
      detail: `${method} ${path} (${detail})`,
    });
    throw aborted ? new Error(`Request timed out: ${method} ${path}`) : err;
  } finally {
    clearTimeout(timeout);
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw apiErrorFromResponse(res.status, text);
  }
  return res.json() as Promise<T>;
}

export async function fetchBlockTree(parentId: string | null): Promise<BlockRecord[]> {
  const query = parentId === null ? '' : `?parentId=${encodeURIComponent(parentId)}`;
  const res = await apiFetch(`/rpc/blocks/tree${query}`);
  const data = await parseJson<{ blocks: BlockRecord[] }>(res);
  return data.blocks;
}

/** Readable pages and databases for the sidebar; only workspace roots carry content. */
export async function fetchNavBlocks(): Promise<BlockRecord[]> {
  const res = await apiFetch('/rpc/blocks/nav');
  const data = await parseJson<{ blocks: BlockRecord[] }>(res);
  return data.blocks;
}

export async function fetchBlock(id: string): Promise<BlockRecord> {
  const res = await apiFetch(`/rpc/blocks/${id}`);
  const data = await parseJson<{ block: BlockRecord }>(res);
  return data.block;
}

export async function createBlock(input: {
  id?: string;
  parentId: string | null;
  type: string;
  title?: string;
  content?: Record<string, unknown>;
  icon?: string;
  position?: number;
}): Promise<BlockRecord> {
  const res = await apiFetch('/rpc/blocks', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  const data = await parseJson<{ block: BlockRecord }>(res);
  return data.block;
}

export async function updateBlock(
  id: string,
  input: {
    title?: string;
    type?: string;
    content?: Record<string, unknown>;
    icon?: string | null;
    expectedSyncVersion?: number;
  },
): Promise<BlockRecord> {
  const res = await apiFetch(`/rpc/blocks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  const data = await parseJson<{ block: BlockRecord }>(res);
  return data.block;
}

export async function deleteBlock(id: string): Promise<void> {
  const res = await apiFetch(`/rpc/blocks/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const text = await res.text();
    throw apiErrorFromResponse(res.status, text || `Delete failed (${res.status})`);
  }
}

export async function restoreBlock(id: string): Promise<BlockRecord> {
  const res = await apiFetch(`/rpc/blocks/${id}/restore`, { method: 'POST' });
  const data = await parseJson<{ block: BlockRecord }>(res);
  return data.block;
}

export async function reorderBlocks(
  updates: Array<{ id: string; position: number }>,
): Promise<BlockRecord[]> {
  const res = await apiFetch('/rpc/blocks/reorder', {
    method: 'POST',
    body: JSON.stringify({ updates }),
  });
  const data = await parseJson<{ blocks: BlockRecord[] }>(res);
  return data.blocks;
}

export async function reparentBlock(
  id: string,
  input: { parentId: string | null; position: number },
): Promise<BlockRecord> {
  const res = await apiFetch(`/rpc/blocks/${id}/reparent`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  const data = await parseJson<{ block: BlockRecord }>(res);
  return data.block;
}

export async function fetchPageDocument(pageId: string): Promise<PageDocument> {
  const cached = getCachedPageDocument(pageId);
  if (cached) {
    return cached;
  }

  const res = await apiFetch(`/rpc/blocks/${pageId}/content`);
  const data = await parseJson<{ block: BlockRecord; contentBlocks: BlockRecord[] }>(res);
  const document: PageDocument = {
    block: data.block,
    contentBlocks: data.contentBlocks,
  };
  setCachedPageDocument(pageId, document);
  return document;
}

export function prefetchPageDocument(pageId: string): void {
  if (getCachedPageDocument(pageId)) {
    return;
  }
  void fetchPageDocument(pageId).catch(() => {
    // Prefetch failures are silent; navigation will retry.
  });
}

export async function fetchPageContentBlocks(pageId: string): Promise<BlockRecord[]> {
  const document = await fetchPageDocument(pageId);
  return document.contentBlocks;
}

export async function fetchFamilyRootId(): Promise<string | null> {
  const roots = await fetchBlockTree(null);
  const family = roots.find((b) => b.type === 'page' && b.title === 'Family');
  return family?.id ?? null;
}

export type LibraryFilter = 'family' | 'private' | 'trash' | 'favorites' | 'llm-private';

export type FavoritePage = {
  id: string;
  title: string;
  type: 'page' | 'database';
  favoritedAt: string;
};

export type LibraryRow = {
  id: string;
  parentId: string | null;
  type: 'page' | 'database';
  title: string;
  icon: string | null;
  createdBy: string;
  updatedAt: string;
  deletedAt: string | null;
  source: 'family' | 'private';
  parentTitle: string | null;
  visitedAt?: string | null;
};

export async function fetchLibraryRows(filter: LibraryFilter): Promise<LibraryRow[]> {
  const res = await apiFetch(`/rpc/library?filter=${encodeURIComponent(filter)}`);
  const data = await parseJson<{ rows: LibraryRow[] }>(res);
  return data.rows;
}

export async function fetchFavorites(): Promise<FavoritePage[]> {
  const res = await apiFetch('/rpc/favorites');
  const data = await parseJson<{ favorites: FavoritePage[] }>(res);
  return data.favorites;
}

export async function starBlock(blockId: string): Promise<void> {
  const res = await apiFetch(`/rpc/favorites/${encodeURIComponent(blockId)}`, {
    method: 'PUT',
    body: '{}',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
}

export async function unstarBlock(blockId: string): Promise<void> {
  const res = await apiFetch(`/rpc/favorites/${encodeURIComponent(blockId)}`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 204) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
}

export type LlmPrivatePage = {
  id: string;
  title: string;
  type: 'page' | 'database';
  markedAt: string;
};

export async function fetchLlmPrivatePages(): Promise<LlmPrivatePage[]> {
  const res = await apiFetch('/rpc/llm-private');
  const data = await parseJson<{ pages: LlmPrivatePage[] }>(res);
  return data.pages;
}

export async function markLlmPrivateBlock(blockId: string): Promise<void> {
  const res = await apiFetch(`/rpc/llm-private/${encodeURIComponent(blockId)}`, {
    method: 'PUT',
    body: '{}',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
}

export async function clearLlmPrivateBlock(blockId: string): Promise<void> {
  const res = await apiFetch(`/rpc/llm-private/${encodeURIComponent(blockId)}`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 204) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
}

export async function fetchMembers(): Promise<MemberRecord[]> {
  const res = await apiFetch('/rpc/members');
  const data = await parseJson<{ members: MemberRecord[] }>(res);
  return data.members;
}

export async function fetchCurrentMember(): Promise<MemberRecord> {
  const res = await apiFetch('/rpc/members/me');
  const data = await parseJson<{ member: MemberRecord }>(res);
  return data.member;
}

export async function updateMemberProfile(input: { displayName: string }): Promise<MemberRecord> {
  const res = await apiFetch('/rpc/members/me', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  const data = await parseJson<{ member: MemberRecord }>(res);
  return data.member;
}

export async function presignUpload(input: {
  blockId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}): Promise<{ fileId: string; uploadUrl: string }> {
  const res = await apiFetch('/rpc/files/presign-upload', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  const data = await parseJson<{ fileId: string; uploadUrl: string }>(res);
  return data;
}

export async function presignDownload(fileId: string): Promise<string> {
  const res = await apiFetch('/rpc/files/presign-download', {
    method: 'POST',
    body: JSON.stringify({ fileId }),
  });
  const data = await parseJson<{ downloadUrl: string }>(res);
  return data.downloadUrl;
}

export type PendingMember = {
  id: string;
  email: string;
  role: string;
  createdAt: string;
};

export type MemberManagement = {
  members: MemberRecord[];
  pending: PendingMember[];
};

export async function fetchMemberManagement(): Promise<MemberManagement> {
  const res = await apiFetch('/rpc/members/manage');
  const data = await parseJson<MemberManagement>(res);
  return data;
}

export async function addMemberEmail(
  email: string,
  role: 'admin' | 'member' = 'member',
): Promise<PendingMember> {
  const res = await apiFetch('/rpc/members/allowlist', {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
  const data = await parseJson<{ entry: PendingMember }>(res);
  return data.entry;
}

export async function removePendingMember(id: string): Promise<void> {
  const res = await apiFetch(`/rpc/members/allowlist/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Remove failed (${res.status})`);
  }
}

export async function removeMember(id: string): Promise<void> {
  const res = await apiFetch(`/rpc/members/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Remove failed (${res.status})`);
  }
}

export type { PageDocument };
